import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, UserPlus, Search, Phone, DollarSign, Clock,
  CheckCircle, RefreshCw, AlertTriangle, TrendingUp,
  X, Calendar, Filter
} from 'lucide-react';
import { getToken } from '../../services/authService';

// ─── Types ──────────────────────────────────────────────────────
interface Lead {
  id: number;
  nombre: string;
  fuente: string;
  producto: string;
  estado: string;
  monto_total: number | null;
  monto_cobrado: number;
  cuotas_total: number;
  cuotas_pagadas: number;
  fecha_primer_contacto: string | null;
  fecha_ultimo_contacto: string | null;
  fecha_followup: string | null;
  fecha_meet: string | null;
  notas: string | null;
  mensaje_followup: string | null;
  cohorte: number | null;
  created_at: string;
  updated_at: string;
}

interface Dashboard {
  meets_semana: number;
  meets_mes: number;
  ventas_mes: number;
  revenue_mes: number;
  revenue_total: number;
  revenue_pendiente: number;
  followups_pendientes: number;
  conversion_rate: number;
  por_estado: { estado: string; total: number }[];
  por_fuente: { fuente: string; total: number }[];
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

// ─── API ────────────────────────────────────────────────────────
const LEADS_API = process.env.REACT_APP_AUTH_API
  ? process.env.REACT_APP_AUTH_API.replace('/auth', '/leads')
  : (process.env.NODE_ENV === 'production' ? '/api/leads' : 'http://localhost:8080/api/leads');

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${getToken()}`
  };
}

// ─── Constants ──────────────────────────────────────────────────
const ESTADOS = [
  { value: 'contactado', label: 'Contactado', color: 'bg-gray-100 text-gray-700' },
  { value: 'meet_agendada', label: 'Meet agendada', color: 'bg-blue-100 text-blue-700' },
  { value: 'meet_hecha', label: 'Meet hecha', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'dijo_si', label: 'Dijo sí', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'primera_cuota', label: '1ra cuota', color: 'bg-orange-100 text-orange-700' },
  { value: 'pagado_completo', label: 'Pagado', color: 'bg-green-100 text-green-700' },
  { value: 'perdido', label: 'Perdido', color: 'bg-red-100 text-red-700' },
];

const FUENTES = ['linkedin', 'tiktok', 'referido', 'web', 'otro'];
const PRODUCTOS = ['bootcamp', 'mentoria', 'mock_interview'];

function estadoBadge(estado: string) {
  const e = ESTADOS.find(s => s.value === estado);
  return e ? e : { label: estado, color: 'bg-gray-100 text-gray-600' };
}

function followupUrgency(fecha: string | null): string {
  if (!fecha) return '';
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const fu = new Date(fecha + 'T00:00:00'); fu.setHours(0, 0, 0, 0);
  const diff = Math.floor((fu.getTime() - today.getTime()) / 86400000);
  if (diff < 0) return 'bg-red-50 border-l-4 border-red-400';
  if (diff === 0) return 'bg-yellow-50 border-l-4 border-yellow-400';
  if (diff <= 3) return 'bg-blue-50 border-l-4 border-blue-300';
  return '';
}

// ─── Component ──────────────────────────────────────────────────
export default function LeadsCRM() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 50, total: 0, total_pages: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [filterFuente, setFilterFuente] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [tab, setTab] = useState<'pipeline' | 'dashboard' | 'seguimiento'>('pipeline');
  const [followupLeads, setFollowupLeads] = useState<Lead[]>([]);
  const [followupRevenue, setFollowupRevenue] = useState<number>(0);
  const [loadingFollowups, setLoadingFollowups] = useState(false);

  // ─── Fetch ──────────────────────────────────────────────────
  const fetchLeads = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.set('page', String(pagination.page));
      params.set('limit', '50');
      if (search) params.set('search', search);
      if (filterEstado) params.set('estado', filterEstado);
      if (filterFuente) params.set('fuente', filterFuente);

      const res = await fetch(`${LEADS_API}/list.php?${params}`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) {
        setLeads(data.data.leads);
        setPagination(data.data.pagination);
      }
    } catch (e) {
      console.error('Error fetching leads:', e);
    }
  }, [pagination.page, search, filterEstado, filterFuente]);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${LEADS_API}/dashboard.php`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setDashboard(data.data ?? data);
    } catch (e) {
      console.error('Error fetching dashboard:', e);
    }
  }, []);

  const fetchFollowupsToday = useCallback(async () => {
    setLoadingFollowups(true);
    try {
      const res = await fetch(`${LEADS_API}/followups-today.php`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success && Array.isArray(data.leads)) {
        setFollowupLeads(data.leads);
        setFollowupRevenue(data.revenue_pendiente ?? 0);
      } else {
        setFollowupLeads([]);
        setFollowupRevenue(0);
      }
    } catch (e) {
      console.error('Error fetching follow-ups:', e);
      setFollowupLeads([]);
    } finally {
      setLoadingFollowups(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchLeads(), fetchDashboard()]).finally(() => setLoading(false));
  }, [fetchLeads, fetchDashboard]);

  useEffect(() => {
    if (tab === 'seguimiento') fetchFollowupsToday();
  }, [tab, fetchFollowupsToday]);

  // ─── Create / Update ───────────────────────────────────────
  const saveLead = async (formData: Partial<Lead>, isNew: boolean) => {
    const url = isNew ? `${LEADS_API}/create.php` : `${LEADS_API}/update.php?id=${formData.id}`;
    const method = isNew ? 'POST' : 'PUT';
    try {
      const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(formData) });
      const data = await res.json();
      if (data.success) {
        setShowCreate(false);
        setEditingLead(null);
        fetchLeads();
        fetchDashboard();
        fetchFollowupsToday();
      } else {
        alert(data.message || 'Error al guardar');
      }
    } catch (e) {
      alert('Error de conexión');
    }
  };

  const quickUpdateEstado = async (lead: Lead, nuevoEstado: string) => {
    await saveLead({ id: lead.id, estado: nuevoEstado } as any, false);
  };

  // ─── Render ─────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-gray-400 hover:text-gray-600" title="Volver al panel"><ArrowLeft className="w-5 h-5" /></Link>
            <h1 className="text-lg font-bold text-gray-800">CRM Leads</h1>
            {dashboard && (
              <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-medium">
                {dashboard.followups_pendientes} follow-ups pendientes
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {(['pipeline', 'seguimiento', 'dashboard'] as const).map(t => (
              <button key={t} onClick={() => setTab(t)}
                className={`text-sm px-3 py-1.5 rounded-lg border transition-colors ${tab === t ? 'bg-purple-100 border-purple-300 text-purple-800' : 'hover:bg-gray-50'}`}>
                {t === 'pipeline' && <><Filter className="w-4 h-4 inline mr-1" />Pipeline</>}
                {t === 'seguimiento' && <><Clock className="w-4 h-4 inline mr-1" />Seguimiento hoy</>}
                {t === 'dashboard' && <><TrendingUp className="w-4 h-4 inline mr-1" />Dashboard</>}
              </button>
            ))}
            <button onClick={() => { setEditingLead(null); setShowCreate(true); }}
              className="bg-purple-600 text-white text-sm px-3 py-1.5 rounded-lg hover:bg-purple-700 flex items-center gap-1">
              <UserPlus className="w-4 h-4" /> Nuevo lead
            </button>
            <button onClick={() => { fetchLeads(); fetchDashboard(); if (tab === 'seguimiento') fetchFollowupsToday(); }} className="p-1.5 rounded-lg border hover:bg-gray-50" title="Actualizar">
              <RefreshCw className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-4">

        {/* ─── Dashboard Tab ──────────────────────────────────── */}
        {tab === 'dashboard' && dashboard && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricCard label="Meets este mes" value={dashboard.meets_mes} icon={<Phone className="w-5 h-5" />} />
              <MetricCard label="Ventas cerradas" value={dashboard.ventas_mes} icon={<CheckCircle className="w-5 h-5" />} />
              <MetricCard label="Revenue cobrado" value={`$${dashboard.revenue_mes.toLocaleString()}`} icon={<DollarSign className="w-5 h-5" />} accent />
              <MetricCard label="Pendiente cobro" value={`$${dashboard.revenue_pendiente.toLocaleString()}`} icon={<AlertTriangle className="w-5 h-5" />} warn={dashboard.revenue_pendiente > 0} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricCard label="Revenue total" value={`$${dashboard.revenue_total.toLocaleString()}`} icon={<TrendingUp className="w-5 h-5" />} />
              <MetricCard label="Conversión meet→venta" value={`${dashboard.conversion_rate}%`} icon={<TrendingUp className="w-5 h-5" />} />
              <MetricCard label="Meets esta semana" value={dashboard.meets_semana} icon={<Calendar className="w-5 h-5" />} />
              <MetricCard label="Follow-ups hoy" value={dashboard.followups_pendientes} icon={<Clock className="w-5 h-5" />} warn={dashboard.followups_pendientes > 0} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <h3 className="text-sm font-semibold text-gray-600 mb-3">Por estado</h3>
                {dashboard.por_estado.map(e => (
                  <div key={e.estado} className="flex justify-between items-center py-1.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${estadoBadge(e.estado).color}`}>{estadoBadge(e.estado).label}</span>
                    <span className="text-sm font-medium text-gray-700">{e.total}</span>
                  </div>
                ))}
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <h3 className="text-sm font-semibold text-gray-600 mb-3">Por fuente</h3>
                {dashboard.por_fuente.map(f => (
                  <div key={f.fuente} className="flex justify-between items-center py-1.5">
                    <span className="text-sm text-gray-600 capitalize">{f.fuente}</span>
                    <span className="text-sm font-medium text-gray-700">{f.total}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ─── Seguimiento hoy (solo leads con follow-up vencido o hoy) ─── */}
        {tab === 'seguimiento' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Leads con fecha de seguimiento hoy o vencida. Solo vos (admin) ves este tablero.
              </p>
              <button onClick={fetchFollowupsToday} disabled={loadingFollowups} className="text-sm text-purple-600 hover:underline flex items-center gap-1">
                <RefreshCw className={`w-4 h-4 ${loadingFollowups ? 'animate-spin' : ''}`} /> Actualizar
              </button>
            </div>
            {followupRevenue > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-sm">
                <span className="text-amber-800 font-medium">Revenue pendiente de esta lista:</span>
                <span className="ml-2 font-semibold text-amber-900">${followupRevenue.toLocaleString()} USD</span>
              </div>
            )}
            {loadingFollowups ? (
              <div className="text-center py-12 text-gray-400">Cargando seguimientos...</div>
            ) : followupLeads.length === 0 ? (
              <div className="text-center py-12 text-gray-400 bg-white rounded-xl border border-gray-100">
                No hay follow-ups pendientes para hoy. ¡Buen trabajo!
              </div>
            ) : (
              <div className="space-y-2">
                {followupLeads.map(lead => (
                  <div key={lead.id}
                    className={`bg-white rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer ${followupUrgency(lead.fecha_followup)}`}
                    onClick={() => { setEditingLead(lead); setShowCreate(true); }}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div>
                          <div className="font-medium text-gray-800 text-sm">{lead.nombre}</div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-gray-400 capitalize">{lead.fuente}</span>
                            <span className="text-xs text-gray-300">|</span>
                            <span className="text-xs text-gray-400">{lead.producto}</span>
                            {lead.cohorte && <><span className="text-xs text-gray-300">|</span><span className="text-xs text-gray-400">C{lead.cohorte}</span></>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {lead.monto_total != null && (
                          <div className="text-right">
                            <div className="text-sm font-semibold text-gray-700">${lead.monto_cobrado ?? 0}/{lead.monto_total}</div>
                          </div>
                        )}
                        {lead.fecha_followup && (
                          <div className="text-right">
                            <div className="text-xs text-gray-400 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(lead.fecha_followup + 'T00:00:00').toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })}
                            </div>
                          </div>
                        )}
                        <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${estadoBadge(lead.estado).color}`}>
                          {estadoBadge(lead.estado).label}
                        </span>
                      </div>
                    </div>
                    {lead.mensaje_followup && <div className="mt-2 text-xs text-purple-600 truncate max-w-xl">💬 {lead.mensaje_followup}</div>}
                    {lead.notas && <div className="mt-1 text-xs text-gray-500 truncate max-w-xl">📝 {lead.notas}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ─── Pipeline Tab ───────────────────────────────────── */}
        {tab === 'pipeline' && (
          <>
            {/* Filters */}
            <div className="flex flex-wrap gap-2 mb-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" placeholder="Buscar por nombre..." value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-purple-300 focus:outline-none" />
              </div>
              <select value={filterEstado} onChange={e => setFilterEstado(e.target.value)}
                className="text-sm border rounded-lg px-3 py-2 bg-white">
                <option value="">Todos los estados</option>
                {ESTADOS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
              </select>
              <select value={filterFuente} onChange={e => setFilterFuente(e.target.value)}
                className="text-sm border rounded-lg px-3 py-2 bg-white">
                <option value="">Todas las fuentes</option>
                {FUENTES.map(f => <option key={f} value={f} className="capitalize">{f}</option>)}
              </select>
            </div>

            {/* Lead list */}
            {loading ? (
              <div className="text-center py-12 text-gray-400">Cargando...</div>
            ) : leads.length === 0 ? (
              <div className="text-center py-12 text-gray-400">No hay leads. Creá el primero.</div>
            ) : (
              <div className="space-y-2">
                {leads.map(lead => (
                  <div key={lead.id}
                    className={`bg-white rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer ${followupUrgency(lead.fecha_followup)}`}
                    onClick={() => { setEditingLead(lead); setShowCreate(true); }}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div>
                          <div className="font-medium text-gray-800 text-sm">{lead.nombre}</div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-gray-400 capitalize">{lead.fuente}</span>
                            <span className="text-xs text-gray-300">|</span>
                            <span className="text-xs text-gray-400">{lead.producto}</span>
                            {lead.cohorte && <><span className="text-xs text-gray-300">|</span><span className="text-xs text-gray-400">C{lead.cohorte}</span></>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {lead.monto_total && (
                          <div className="text-right">
                            <div className="text-sm font-semibold text-gray-700">${lead.monto_cobrado}/{lead.monto_total}</div>
                            <div className="text-xs text-gray-400">{lead.cuotas_pagadas}/{lead.cuotas_total} cuotas</div>
                          </div>
                        )}
                        {lead.fecha_followup && (
                          <div className="text-right">
                            <div className="text-xs text-gray-400 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(lead.fecha_followup + 'T00:00:00').toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })}
                            </div>
                          </div>
                        )}
                        <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${estadoBadge(lead.estado).color}`}>
                          {estadoBadge(lead.estado).label}
                        </span>
                      </div>
                    </div>
                    {lead.notas && <div className="mt-2 text-xs text-gray-500 truncate max-w-xl">{lead.notas}</div>}
                  </div>
                ))}
              </div>
            )}

            {pagination.total_pages > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                {Array.from({ length: pagination.total_pages }, (_, i) => (
                  <button key={i} onClick={() => setPagination(p => ({ ...p, page: i + 1 }))}
                    className={`px-3 py-1 text-sm rounded ${pagination.page === i + 1 ? 'bg-purple-600 text-white' : 'bg-white border hover:bg-gray-50'}`}>
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* ─── Modal Create/Edit ──────────────────────────────── */}
      {showCreate && (
        <LeadModal
          lead={editingLead}
          onSave={(data) => saveLead(data, !editingLead)}
          onClose={() => { setShowCreate(false); setEditingLead(null); }}
          onQuickStatus={editingLead ? (estado) => quickUpdateEstado(editingLead, estado) : undefined}
        />
      )}
    </div>
  );
}

// ─── Metric Card ──────────────────────────────────────────────
function MetricCard({ label, value, icon, accent, warn }: { label: string; value: string | number; icon: React.ReactNode; accent?: boolean; warn?: boolean }) {
  return (
    <div className={`rounded-xl p-4 shadow-sm ${warn ? 'bg-red-50 border border-red-200' : accent ? 'bg-purple-50 border border-purple-200' : 'bg-white'}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className={warn ? 'text-red-400' : accent ? 'text-purple-400' : 'text-gray-400'}>{icon}</span>
        <span className="text-xs text-gray-500">{label}</span>
      </div>
      <div className={`text-2xl font-bold ${warn ? 'text-red-700' : accent ? 'text-purple-700' : 'text-gray-800'}`}>{value}</div>
    </div>
  );
}

// ─── Lead Modal ───────────────────────────────────────────────
function LeadModal({ lead, onSave, onClose, onQuickStatus }: {
  lead: Lead | null;
  onSave: (data: Partial<Lead>) => void;
  onClose: () => void;
  onQuickStatus?: (estado: string) => void;
}) {
  const isNew = !lead;
  const [form, setForm] = useState({
    nombre: lead?.nombre || '',
    fuente: lead?.fuente || 'linkedin',
    producto: lead?.producto || 'bootcamp',
    estado: lead?.estado || 'contactado',
    monto_total: lead?.monto_total || '',
    monto_cobrado: lead?.monto_cobrado || 0,
    cuotas_total: lead?.cuotas_total || 1,
    cuotas_pagadas: lead?.cuotas_pagadas || 0,
    fecha_primer_contacto: lead?.fecha_primer_contacto || new Date().toISOString().split('T')[0],
    fecha_ultimo_contacto: lead?.fecha_ultimo_contacto || new Date().toISOString().split('T')[0],
    fecha_followup: lead?.fecha_followup || '',
    fecha_meet: lead?.fecha_meet || '',
    notas: lead?.notas || '',
    mensaje_followup: lead?.mensaje_followup || '',
    cohorte: lead?.cohorte || '',
  });

  const set = (field: string, value: any) => setForm(f => ({ ...f, [field]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload: any = { ...form };
    if (lead) payload.id = lead.id;
    if (payload.monto_total === '') payload.monto_total = null;
    if (payload.fecha_followup === '') payload.fecha_followup = null;
    if (payload.fecha_meet === '') payload.fecha_meet = null;
    if (payload.cohorte === '') payload.cohorte = null;
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-8 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 mb-8">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="font-bold text-gray-800">{isNew ? 'Nuevo lead' : `Editar: ${lead.nombre}`}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
        </div>

        {/* Quick status buttons for existing leads */}
        {!isNew && onQuickStatus && (
          <div className="px-4 pt-3 flex flex-wrap gap-1.5">
            {ESTADOS.map(e => (
              <button key={e.value}
                onClick={() => { onQuickStatus(e.value); onClose(); }}
                className={`text-xs px-2.5 py-1 rounded-full transition-all ${form.estado === e.value ? e.color + ' ring-2 ring-offset-1 ring-purple-300' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                {e.label}
              </button>
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs font-medium text-gray-500">Nombre</label>
              <input type="text" required value={form.nombre} onChange={e => set('nombre', e.target.value)}
                className="w-full mt-1 px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-purple-300 focus:outline-none" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Fuente</label>
              <select value={form.fuente} onChange={e => set('fuente', e.target.value)}
                className="w-full mt-1 px-3 py-2 text-sm border rounded-lg bg-white">
                {FUENTES.map(f => <option key={f} value={f} className="capitalize">{f}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Producto</label>
              <select value={form.producto} onChange={e => set('producto', e.target.value)}
                className="w-full mt-1 px-3 py-2 text-sm border rounded-lg bg-white">
                {PRODUCTOS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Estado</label>
              <select value={form.estado} onChange={e => set('estado', e.target.value)}
                className="w-full mt-1 px-3 py-2 text-sm border rounded-lg bg-white">
                {ESTADOS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Cohorte</label>
              <input type="number" value={form.cohorte} onChange={e => set('cohorte', e.target.value)}
                className="w-full mt-1 px-3 py-2 text-sm border rounded-lg" placeholder="1, 2..." />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500">Monto total (USD)</label>
              <input type="number" step="0.01" value={form.monto_total} onChange={e => set('monto_total', e.target.value)}
                className="w-full mt-1 px-3 py-2 text-sm border rounded-lg" placeholder="495" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Monto cobrado (USD)</label>
              <input type="number" step="0.01" value={form.monto_cobrado} onChange={e => set('monto_cobrado', Number(e.target.value))}
                className="w-full mt-1 px-3 py-2 text-sm border rounded-lg" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Cuotas total</label>
              <input type="number" value={form.cuotas_total} onChange={e => set('cuotas_total', Number(e.target.value))}
                className="w-full mt-1 px-3 py-2 text-sm border rounded-lg" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Cuotas pagadas</label>
              <input type="number" value={form.cuotas_pagadas} onChange={e => set('cuotas_pagadas', Number(e.target.value))}
                className="w-full mt-1 px-3 py-2 text-sm border rounded-lg" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500">Fecha meet</label>
              <input type="date" value={form.fecha_meet} onChange={e => set('fecha_meet', e.target.value)}
                className="w-full mt-1 px-3 py-2 text-sm border rounded-lg" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Fecha follow-up</label>
              <input type="date" value={form.fecha_followup} onChange={e => set('fecha_followup', e.target.value)}
                className="w-full mt-1 px-3 py-2 text-sm border rounded-lg" />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-500">Notas</label>
            <textarea rows={2} value={form.notas} onChange={e => set('notas', e.target.value)}
              className="w-full mt-1 px-3 py-2 text-sm border rounded-lg resize-none" placeholder="Contexto, dolor principal, objeciones..." />
          </div>

          <div>
            <label className="text-xs font-medium text-gray-500">Mensaje sugerido follow-up</label>
            <textarea rows={2} value={form.mensaje_followup} onChange={e => set('mensaje_followup', e.target.value)}
              className="w-full mt-1 px-3 py-2 text-sm border rounded-lg resize-none" placeholder="El mensaje que le vas a mandar..." />
          </div>

          <div className="flex gap-2 pt-2">
            <button type="submit"
              className="flex-1 bg-purple-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors">
              {isNew ? 'Crear lead' : 'Guardar cambios'}
            </button>
            <button type="button" onClick={onClose}
              className="px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
